/**
 * Beheer van specialisatieprofielen.
 *
 * Eén lijst voor twee herkomsten. Een profiel dat bij een pack hoort is
 * read-only — niet omdat je er geen rechten op hebt, maar omdat de pack de
 * bron is en een handmatige wijziging bij de volgende sync zou verdwijnen.
 * Dat verschil wordt in de lijst getoond, niet verstopt: je ziet wél wat er
 * geldt, je kunt het alleen niet hier veranderen.
 *
 * De attribuut-bouwer komt uit `SubtypesSettings` in plaats van dat er een
 * tweede versie naast ligt — twee bouwers die uit elkaar lopen is precies het
 * soort drift dat deze codebase al genoeg heeft gehad.
 */

import type { AttributeSchema } from "@ae/domain";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../api/client";
import { listIconNames } from "../editor/canvas/archimateIconLibrary";
import { confirmDialog } from "../editor/confirmDialog";
import { colorForConcept } from "../editor/layerColors";
import { getRegistry, resolveBilingual } from "../registry";
import { useSession } from "../state/session";
import { useWorkspaceData } from "../state/workspace";
import { type AttrDraft, AttributeBuilder, draftToSchema, schemaToDraft } from "./attributeBuilder";
import { ld, pill } from "./ledgerUi";

interface ProfileRow {
  name: string;
  label?: { nl?: string; en?: string };
  description?: { nl?: string; en?: string };
  applies_to: string[];
  attributes?: unknown[];
  icon_id?: string | null;
  notation?: { fill?: string; stroke?: string; text_color?: string } | null;
  pack_id: string | null;
  editable: boolean;
}

const EMPTY_FORM = {
  name: "",
  labelNl: "",
  labelEn: "",
  appliesTo: [] as string[],
  iconId: "",
  // Leeg = geen eigen kleur; het profiel volgt dan de laagkleur van z'n
  // basistype, precies zoals vandaag.
  fill: "",
  stroke: "",
  textColor: "",
  attrs: [] as AttrDraft[],
};

export function ProfilesSettings() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);

  const [rows, setRows] = useState<ProfileRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [inspecting, setInspecting] = useState<string | null>(null);

  const elementTypes = useMemo(
    () =>
      getRegistry()
        .listActiveElementTypes()
        .slice()
        .sort((a, b) =>
          resolveBilingual(a.label, lang).localeCompare(resolveBilingual(b.label, lang)),
        ),
    [lang],
  );
  const iconNames = useMemo(() => listIconNames(), []);

  const reload = useCallback(async () => {
    if (!workspaceId || !branchId) return;
    setLoading(true);
    try {
      const res = await api.listProfiles(workspaceId, branchId);
      setRows(res.profiles as ProfileRow[]);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("profiles.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [workspaceId, branchId, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const startNew = () => {
    setEditing("__new__");
    setForm({ ...EMPTY_FORM });
  };

  const startEdit = (row: ProfileRow) => {
    setEditing(row.name);
    setForm({
      name: row.name,
      labelNl: row.label?.nl ?? "",
      labelEn: row.label?.en ?? "",
      appliesTo: row.applies_to,
      iconId: row.icon_id ?? "",
      fill: row.notation?.fill ?? "",
      stroke: row.notation?.stroke ?? "",
      textColor: row.notation?.text_color ?? "",
      attrs: ((row.attributes ?? []) as AttributeSchema[]).map(schemaToDraft),
    });
  };

  const save = async () => {
    if (!workspaceId || !branchId) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    const body = {
      label: { nl: form.labelNl || form.name, en: form.labelEn || form.name },
      applies_to: form.appliesTo,
      attributes: form.attrs.map(draftToSchema),
      icon_id: form.iconId,
      notation: {
        ...(form.fill ? { fill: form.fill } : {}),
        ...(form.stroke ? { stroke: form.stroke } : {}),
        ...(form.textColor ? { text_color: form.textColor } : {}),
      },
    };
    try {
      if (editing === "__new__") {
        await api.createProfile(workspaceId, branchId, { ...body, name: form.name.trim() });
      } else if (editing) {
        const renaming = form.name.trim() !== editing;
        // Hernoemen raakt elk object dat het profiel draagt. Dat hoort niet
        // stil te gebeuren, ook niet als het goed gaat.
        if (
          renaming &&
          !(await confirmDialog({
            message: t("profiles.confirmRename", { from: editing, to: form.name.trim() }),
          }))
        ) {
          setBusy(false);
          return;
        }
        const res = await api.updateProfile(workspaceId, branchId, editing, {
          ...body,
          ...(renaming ? { new_name: form.name.trim() } : {}),
        });
        if (res.renamed_from) {
          const r = res.references ?? { concepts: 0, relations: 0, diagrams: 0 };
          setNotice(
            t("profiles.renameDone", {
              from: res.renamed_from,
              to: res.name,
              concepts: r.concepts,
              relations: r.relations,
              diagrams: r.diagrams,
            }) +
              // De viewState-melding alleen tonen als er iets te melden is;
              // anders leest 'm niemand meer als het een keer wél telt.
              (res.view_state_mentions
                ? ` ${t("profiles.renameViewStateWarning", { count: res.view_state_mentions })}`
                : ""),
          );
        }
      }
      setEditing(null);
      await reload();
      // De registry draagt de profielen; zonder herladen blijft de oude
      // definitie in de keuzelijsten staan tot de volgende hydrate.
      await useWorkspaceData.getState().hydrate(workspaceId, branchId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("profiles.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (name: string) => {
    if (!workspaceId || !branchId) return;
    if (!(await confirmDialog({ message: t("profiles.confirmDelete", { name }), danger: true })))
      return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteProfile(workspaceId, branchId, name);
      await reload();
      await useWorkspaceData.getState().hydrate(workspaceId, branchId);
    } catch (err) {
      // De server weigert een profiel dat nog ergens op zit, mét de aantallen.
      // Die melding is bruikbaarder dan een generieke — dus letterlijk tonen.
      setError(err instanceof ApiError ? err.message : t("profiles.deleteFailed"));
    } finally {
      setBusy(false);
    }
  };

  const visible = rows.filter(
    (r) =>
      filter.trim() === "" ||
      r.name.toLowerCase().includes(filter.toLowerCase()) ||
      resolveBilingual(r.label ?? {}, lang)
        .toLowerCase()
        .includes(filter.toLowerCase()),
  );
  const own = visible.filter((r) => r.editable);
  const fromPacks = visible.filter((r) => !r.editable);

  const typeLabel = (id: string) => {
    const et = getRegistry().getElementType(id);
    return et ? resolveBilingual(et.label, lang) : id;
  };

  return (
    <div className="flex flex-col gap-5 text-sm">
      <p className={ld.viewIntro}>{t("profiles.intro")}</p>

      <div aria-live="polite">
        {error && (
          <p role="alert" className={ld.error}>
            {error}
          </p>
        )}
        {notice && <p className={ld.success}>{notice}</p>}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="search"
          aria-label={t("profiles.searchPlaceholder")}
          className={`${ld.inputInline} min-w-0 flex-1`}
          placeholder={t("profiles.searchPlaceholder")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <button type="button" className={ld.primary} onClick={startNew} disabled={busy}>
          {t("profiles.new")}
        </button>
      </div>

      {editing && (
        <section className="flex flex-col gap-4 rounded-lg border border-(--ld-border) bg-(--ld-raised) p-4">
          <h3 className={ld.sectionTitle}>
            {editing === "__new__" ? t("profiles.formNew") : t("profiles.formEdit")}
          </h3>
          <div>
            <label htmlFor="profile-name" className={ld.label}>
              {t("profiles.fieldName")}
            </label>
            <input
              id="profile-name"
              aria-describedby="profile-name-hint"
              className={`${ld.input} font-mono`}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <p id="profile-name-hint" className={ld.hint}>
              {editing === "__new__" ? t("profiles.fieldNameHint") : t("profiles.fieldRenameHint")}
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="profile-label-nl" className={ld.label}>
                {t("profiles.fieldLabelNl")}
              </label>
              <input
                id="profile-label-nl"
                className={ld.input}
                value={form.labelNl}
                onChange={(e) => setForm({ ...form, labelNl: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="profile-label-en" className={ld.label}>
                {t("profiles.fieldLabelEn")}
              </label>
              <input
                id="profile-label-en"
                className={ld.input}
                value={form.labelEn}
                onChange={(e) => setForm({ ...form, labelEn: e.target.value })}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="profile-applies-to" className={ld.label}>
                {t("profiles.fieldAppliesTo")}
              </label>
              <select
                id="profile-applies-to"
                multiple
                size={6}
                aria-describedby="profile-applies-to-hint"
                className={`${ld.input} h-auto py-1`}
                value={form.appliesTo}
                onChange={(e) =>
                  setForm({
                    ...form,
                    appliesTo: [...e.target.selectedOptions].map((o) => o.value),
                  })
                }
              >
                {elementTypes.map((et) => (
                  <option key={et.id} value={et.id}>
                    {resolveBilingual(et.label, lang)}
                  </option>
                ))}
              </select>
              <p id="profile-applies-to-hint" className={ld.hint}>
                {t("profiles.fieldAppliesToHint")}
              </p>
            </div>

            <div>
              <label htmlFor="profile-icon" className={ld.label}>
                {t("profiles.fieldIcon")}
              </label>
              <select
                id="profile-icon"
                aria-describedby="profile-icon-hint"
                className={ld.input}
                value={form.iconId}
                onChange={(e) => setForm({ ...form, iconId: e.target.value })}
              >
                <option value="">{t("profiles.iconNone")}</option>
                {iconNames.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <p id="profile-icon-hint" className={ld.hint}>
                {t("profiles.fieldIconHint")}
              </p>
            </div>
          </div>

          <fieldset>
            <legend className={ld.label}>{t("profiles.fieldColors")}</legend>
            <div className="mt-2 flex flex-wrap items-end gap-4">
              <ColorField
                label={t("profiles.colorFill")}
                value={form.fill}
                onChange={(v) => setForm({ ...form, fill: v })}
              />
              <ColorField
                label={t("profiles.colorStroke")}
                value={form.stroke}
                onChange={(v) => setForm({ ...form, stroke: v })}
              />
              <ColorField
                label={t("profiles.colorText")}
                value={form.textColor}
                onChange={(v) => setForm({ ...form, textColor: v })}
              />
              {/* Voorbeeld in de gekozen kleuren van het profiel zelf. */}
              <div
                className="flex h-9 min-w-24 items-center justify-center rounded-md border px-3 text-xs"
                style={{
                  background: form.fill || "#ffffff",
                  borderColor: form.stroke || form.fill || "#cbd5e1",
                  color: form.textColor || "#333333",
                }}
              >
                {form.labelNl || form.name || t("profiles.colorPreview")}
              </div>
            </div>
            <p className={ld.hint}>{t("profiles.fieldColorsHint")}</p>
          </fieldset>

          <AttributeBuilder
            drafts={form.attrs}
            onChange={(next) => setForm({ ...form, attrs: next })}
          />

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className={ld.secondary}
              onClick={() => setEditing(null)}
              disabled={busy}
            >
              {t("profiles.cancel")}
            </button>
            <button
              type="button"
              className={ld.primary}
              onClick={() => void save()}
              disabled={
                busy || form.appliesTo.length === 0 || (editing === "__new__" && !form.name)
              }
            >
              {busy ? t("profiles.saving") : t("profiles.save")}
            </button>
          </div>
        </section>
      )}

      {loading ? (
        <p className={ld.muted}>{t("profiles.loading")}</p>
      ) : (
        <>
          <section>
            <h3 className={`${ld.sectionTitle} mb-2`}>
              {t("profiles.sectionOwn", { count: own.length })}
            </h3>
            {own.length === 0 ? (
              <p className={`${ld.well} text-(--ld-muted) text-sm`}>{t("profiles.noneOwn")}</p>
            ) : (
              <ul className={ld.list}>
                {own.map((r) => (
                  <li
                    key={r.name}
                    className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{resolveBilingual(r.label ?? {}, lang)}</span>
                        <span className="font-mono text-(--ld-muted) text-xs">{r.name}</span>
                      </div>
                      <p className="mt-0.5 truncate text-(--ld-muted) text-xs">
                        {r.applies_to.map(typeLabel).join(", ")}
                        {r.icon_id && ` · ${t("profiles.fieldIcon")}: ${r.icon_id}`}
                      </p>
                    </div>
                    <span className="flex shrink-0 items-center gap-4">
                      <button
                        type="button"
                        className={ld.textDanger}
                        onClick={() => void remove(r.name)}
                        disabled={busy}
                      >
                        {t("profiles.delete")}
                      </button>
                      <button
                        type="button"
                        className={ld.secondarySm}
                        onClick={() => startEdit(r)}
                        disabled={busy}
                      >
                        {t("profiles.edit")}
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className={ld.sectionTitle}>
              {t("profiles.sectionPacks", { count: fromPacks.length })}
            </h3>
            <p className={`${ld.sectionIntro} mb-2`}>{t("profiles.packsHint")}</p>
            <ul className={`max-h-80 overflow-y-auto ${ld.list}`}>
              {fromPacks.map((r) => (
                <li key={r.name} className="px-4 py-2">
                  <div className="flex items-center gap-3">
                    <span className="min-w-0 flex-1 truncate">
                      {resolveBilingual(r.label ?? {}, lang)}
                      <span className="ml-2 font-mono text-(--ld-muted) text-xs">{r.name}</span>
                    </span>
                    <span className={pill.neutral}>{r.pack_id}</span>
                    <button
                      type="button"
                      className={`shrink-0 text-xs ${ld.textButton}`}
                      aria-expanded={inspecting === r.name}
                      onClick={() => setInspecting(inspecting === r.name ? null : r.name)}
                    >
                      {inspecting === r.name ? t("profiles.hideDetails") : t("profiles.inspect")}
                    </button>
                  </div>
                  {inspecting === r.name && <ProfileDetails row={r} typeLabel={typeLabel} />}
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}

/** Kleurkiezer met een wis-knop. Leeg laten is een echte keuze — dan volgt het
 *  profiel de laagkleur — en dat kan een `<input type="color">` niet uitdrukken,
 *  want die heeft altijd een waarde. */
function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <label className="flex flex-col gap-1 text-(--ld-muted) text-xs">
      {label}
      <span className="flex items-center gap-1.5">
        <input
          type="color"
          className="h-9 w-12 cursor-pointer rounded-md border border-(--ld-control) bg-(--ld-surface) p-0.5"
          value={value || "#ffffff"}
          onChange={(e) => onChange(e.target.value)}
        />
        {value && (
          <button
            type="button"
            className={ld.textButton}
            onClick={() => onChange("")}
            title={t("profiles.colorClear")}
            aria-label={t("profiles.colorClear")}
          >
            ×
          </button>
        )}
      </span>
    </label>
  );
}

/**
 * Alleen-lezen weergave van een pack-profiel: dezelfde velden die je bij een
 * eigen profiel kunt invullen, zodat je kunt zien hoe het DOM het doet zonder
 * dat je eraan kunt draaien.
 *
 * De kleur wordt niet uit `notation` afgelezen maar via `colorForConcept`
 * bepaald — precies zoals het canvas het doet. Voor een pack-profiel wint
 * namelijk het notation-token, en dan zou het `notation`-blok in de pack een
 * kleur tonen die je nergens terugziet.
 */
function ProfileDetails({
  row,
  typeLabel,
}: {
  row: ProfileRow;
  typeLabel: (id: string) => string;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const attrs = (row.attributes ?? []) as AttributeSchema[];
  const appliesTo = row.applies_to.map((entry) =>
    typeof entry === "string" ? entry : ((entry as { type_id?: string }).type_id ?? ""),
  );
  const firstType = appliesTo.find((id) => id && id !== "*");
  const swatch = firstType
    ? colorForConcept({
        typeId: firstType,
        layer: getRegistry().getElementType(firstType)?.layer ?? "business",
        stereotypes: [row.name],
      })
    : null;

  return (
    <div className="mt-2 mb-1 flex flex-col gap-3 rounded-md bg-(--ld-raised) p-3 text-[13px]">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1">
        <dt className="text-(--ld-muted)">{t("profiles.fieldLabelNl")}</dt>
        <dd>{row.label?.nl || "—"}</dd>
        <dt className="text-(--ld-muted)">{t("profiles.fieldLabelEn")}</dt>
        <dd>{row.label?.en || "—"}</dd>
        <dt className="text-(--ld-muted)">{t("profiles.detailDescription")}</dt>
        <dd>{resolveBilingual(row.description ?? {}, lang) || "—"}</dd>
        <dt className="text-(--ld-muted)">{t("profiles.fieldAppliesTo")}</dt>
        <dd>
          {appliesTo.length === 0
            ? "—"
            : appliesTo
                .map((id) =>
                  id === "*" ? t("profiles.detailAnyType") : `${typeLabel(id)} (${id})`,
                )
                .join(", ")}
        </dd>
        <dt className="text-(--ld-muted)">{t("profiles.fieldIcon")}</dt>
        <dd>{row.icon_id || t("profiles.iconNone")}</dd>
        <dt className="text-(--ld-muted)">{t("profiles.fieldColors")}</dt>
        <dd>
          {swatch ? (
            <span className="flex items-center gap-2">
              <span
                className="inline-flex h-6 min-w-16 items-center justify-center rounded border px-1.5 font-mono text-xs"
                style={{ background: swatch.bg, borderColor: swatch.border, color: swatch.text }}
              >
                {swatch.bg}
              </span>
              <span className="text-(--ld-muted)">{t("profiles.detailColorHint")}</span>
            </span>
          ) : (
            "—"
          )}
        </dd>
      </dl>

      <div>
        <p className="mb-1 font-medium">
          {t("profiles.detailAttributes", { count: attrs.length })}
        </p>
        {attrs.length === 0 ? (
          <p className="text-(--ld-muted)">{t("profiles.detailNoAttributes")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="text-left text-(--ld-muted)">
                  <th className="py-1 pr-3 font-normal">{t("profiles.detailAttrKey")}</th>
                  <th className="py-1 pr-3 font-normal">{t("profiles.detailAttrLabel")}</th>
                  <th className="py-1 pr-3 font-normal">{t("profiles.detailAttrType")}</th>
                  <th className="py-1 font-normal">{t("profiles.detailAttrOptions")}</th>
                </tr>
              </thead>
              <tbody>
                {attrs.map((a) => (
                  <tr key={a.name} className="border-(--ld-border) border-t align-top">
                    <td className="py-1 pr-3 font-mono">{a.name}</td>
                    <td className="py-1 pr-3">{resolveBilingual(a.label ?? {}, lang) || "—"}</td>
                    <td className="py-1 pr-3">
                      {/* Een `?`-suffix betekent optioneel; zonder is het veld verplicht. */}
                      {a.type}
                      {a.type.endsWith("?") ? "" : ` · ${t("profiles.detailRequired")}`}
                    </td>
                    <td className="py-1 text-(--ld-muted)">
                      {Array.isArray(a.options) && a.options.length > 0
                        ? a.options.join(", ")
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
